import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
    Sparkles, AlertTriangle, TrendingUp, Award, Phone, Mail,
    Activity, Zap, ArrowRight, Clock, Target,
} from 'lucide-react';
import AIInsightCard from '../../components/ui/AIInsightCard';
import AnimatedCounter from '../../components/ui/AnimatedCounter';
import SparklineChart from '../../components/ui/SparklineChart';

// Demo realistic data — what a Bordeaux agency manager would actually see
const DEMO_DATA = {
    weekLabel: 'Semaine 22 · mai 2026',
    manager: 'Sophie',
    healthScore: 78,
    healthDelta: 3,
    ca_month: 47200,
    ca_target: 26000,
    pipelineValue: 312000,
    activeMandates: 21,
    soldThisMonth: 4,
    briefing: {
        priorities_count: 3,
        exposed_amount: 18400, // amount actually exposed (mandat actif sans contact + compromis en attente)
        exposed_label: '€ de pipeline exposé',
    },
    insights: {
        urgent: [
            {
                icon: Phone,
                headline: 'Mandat exclusif à risque · M. Girard',
                metricValue: 8500,
                metricPrefix: '−',
                metricSuffix: ' € exposés',
                why: '3 rue Garibaldi, Montpellier — 48 jours sans contact. L\'exclusivité arrive à échéance dans 12 jours.',
                ctaLabel: 'Planifier l\'appel',
                sparkline: [3, 4, 5, 6, 7, 8, 9],
            },
            {
                icon: AlertTriangle,
                headline: '2 compromis en attente notaire',
                metricValue: 18400,
                metricPrefix: '',
                metricSuffix: ' € en attente',
                why: '63 av. du Prado + 12 rue Foch. Financement bloqué côté acquéreur, relance bancaire à enclencher.',
                ctaLabel: 'Suivre les dossiers',
                sparkline: [5, 4, 6, 5, 7, 8, 7],
            },
        ],
        opportunity: [
            {
                icon: TrendingUp,
                headline: 'Conversion estim → mandat à booster',
                metricValue: 12300,
                metricPrefix: '+',
                metricSuffix: ' € / an estimés',
                why: 'Votre taux estim → mandat est à 30 % vs Top 20 % à 40 %. Retravailler l\'argumentaire post-visite pourrait débloquer ce gap.',
                ctaLabel: 'Voir l\'argumentaire IA',
                sparkline: [2, 3, 4, 4, 5, 6, 7],
            },
            {
                icon: Zap,
                headline: 'Renforcer le taux d\'exclusivité',
                metricValue: 22000,
                metricPrefix: '+',
                metricSuffix: ' € / an estimés',
                why: 'Taux d\'exclusivité à 45 % aujourd\'hui · objectif santé 50 %. Coachez Léa et Marc sur la négociation exclu.',
                ctaLabel: 'Programmer un coaching',
                sparkline: [3, 4, 5, 5, 6, 6, 7],
            },
        ],
        win: [
            {
                icon: Award,
                headline: 'Top performance · Emma Rousseau',
                metricValue: 14200,
                metricPrefix: '+',
                metricSuffix: ' € ce mois',
                why: '2 ventes signées en 11 jours, taux de joignabilité à 89 %. Un profil à mettre en avant en réunion d\'équipe.',
                ctaLabel: 'Voir son cockpit',
                sparkline: [4, 5, 5, 6, 7, 7, 8],
            },
        ],
    },
};

export default function AIInsightsPreviewPage() {
    const d = DEMO_DATA;
    const [now, setNow] = useState(new Date());
    useEffect(() => {
        const t = setInterval(() => setNow(new Date()), 60000);
        return () => clearInterval(t);
    }, []);

    const scoreColor = d.healthScore >= 75 ? '#10B981' : d.healthScore >= 50 ? '#FF914D' : '#EF4444';

    return (
        <div
            className="min-h-screen text-white"
            style={{
                background: 'radial-gradient(circle at 20% 0%, rgba(104,23,147,0.12) 0%, transparent 50%), radial-gradient(circle at 80% 100%, rgba(255,145,77,0.08) 0%, transparent 50%), #0A0A0A',
                fontFamily: 'IBM Plex Sans, Manrope, sans-serif',
            }}
            data-testid="ai-insights-preview"
        >
            {/* Top bar */}
            <header className="border-b border-white/5 bg-black/40 backdrop-blur-xl sticky top-0 z-20">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#681793] to-[#FF914D] flex items-center justify-center font-black text-white">
                            H
                        </div>
                        <div>
                            <p className="text-xs text-white/40 uppercase tracking-widest" style={{ fontFamily: 'Space Grotesk' }}>
                                Homly · Mock Preview
                            </p>
                            <p className="text-sm font-medium">Synthèse IA Hebdo — Design System "Control Room"</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-white/40">
                        <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                        Mise à jour {now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-6 py-10 space-y-10">
                {/* ─── BRIEFING IA HEADER (the floating glass pill) ─── */}
                <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="relative overflow-hidden rounded-2xl p-5 border border-white/10"
                    style={{
                        background:
                            'linear-gradient(135deg, rgba(104,23,147,0.15) 0%, rgba(255,145,77,0.08) 100%)',
                    }}
                    data-testid="ai-briefing-header"
                >
                    {/* Animated border gradient — running outline */}
                    <div
                        className="absolute inset-0 rounded-2xl pointer-events-none opacity-40"
                        style={{
                            background:
                                'conic-gradient(from 0deg at 50% 50%, transparent, #681793, transparent, #FF914D, transparent)',
                            animation: 'spin 8s linear infinite',
                            WebkitMask:
                                'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                            mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                            WebkitMaskComposite: 'xor',
                            maskComposite: 'exclude',
                            padding: '1px',
                        }}
                    />
                    <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex items-start gap-4">
                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#681793] to-[#FF914D] flex items-center justify-center shrink-0">
                                <Sparkles className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <p
                                    className="text-xs uppercase tracking-widest text-white/40 mb-1"
                                    style={{ fontFamily: 'Space Grotesk' }}
                                >
                                    Briefing du jour · Pilote · IA Homly
                                </p>
                                <h2
                                    className="text-xl md:text-2xl font-semibold text-white leading-tight tracking-tight"
                                    style={{ fontFamily: 'Space Grotesk' }}
                                >
                                    Bonjour {d.manager}.{' '}
                                    <span style={{ fontFamily: 'JetBrains Mono' }} className="text-[#FF914D]">
                                        {d.briefing.priorities_count} priorités
                                    </span>{' '}
                                    identifiées ·{' '}
                                    <span
                                        style={{ fontFamily: 'JetBrains Mono' }}
                                        className="text-white"
                                    >
                                        <AnimatedCounter value={d.briefing.exposed_amount} suffix=" €" />
                                    </span>{' '}
                                    <span className="text-white/50 text-base font-normal">
                                        {d.briefing.exposed_label}
                                    </span>
                                </h2>
                            </div>
                        </div>
                        <button
                            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white text-black font-semibold text-sm hover:bg-white/90 transition-all shadow-lg"
                            data-testid="ai-briefing-trigger"
                            style={{ fontFamily: 'Space Grotesk' }}
                        >
                            Commencer
                            <ArrowRight className="w-4 h-4" />
                        </button>
                    </div>
                </motion.div>

                {/* ─── HEALTH SCORE HERO ─── */}
                <motion.section
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    className="grid grid-cols-1 md:grid-cols-3 gap-4"
                >
                    {/* Big health score */}
                    <div
                        className="md:col-span-2 relative overflow-hidden p-8 rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-xl"
                        data-testid="ai-weekly-health-score"
                    >
                        <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full blur-3xl opacity-30"
                            style={{ background: scoreColor }} />
                        <p
                            className="text-xs uppercase tracking-widest text-white/40 mb-3"
                            style={{ fontFamily: 'Space Grotesk' }}
                        >
                            Score santé agence · {d.weekLabel}
                        </p>
                        <div className="flex items-end gap-6">
                            <div className="flex items-baseline gap-1">
                                <span
                                    className="text-7xl md:text-8xl font-medium tracking-tighter text-white"
                                    style={{ fontFamily: 'JetBrains Mono' }}
                                >
                                    <AnimatedCounter value={d.healthScore} duration={1800} />
                                </span>
                                <span
                                    className="text-3xl text-white/30 font-medium"
                                    style={{ fontFamily: 'JetBrains Mono' }}
                                >
                                    /100
                                </span>
                            </div>
                            <div className="pb-3 flex flex-col gap-1">
                                <span
                                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-bold bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 w-fit"
                                    style={{ fontFamily: 'JetBrains Mono' }}
                                >
                                    <TrendingUp className="w-3 h-3" />
                                    +{d.healthDelta} pts
                                </span>
                                <span
                                    className="text-xs text-white/40"
                                    style={{ fontFamily: 'Space Grotesk' }}
                                >
                                    vs S-1
                                </span>
                            </div>
                        </div>
                        <div className="mt-6 flex items-center gap-4">
                            <div className="flex-1 h-1.5 rounded-full bg-white/5 overflow-hidden">
                                <motion.div
                                    initial={{ width: 0 }}
                                    animate={{ width: `${d.healthScore}%` }}
                                    transition={{ duration: 1.6, ease: 'easeOut' }}
                                    className="h-full rounded-full"
                                    style={{ background: `linear-gradient(90deg, #681793, ${scoreColor})` }}
                                />
                            </div>
                            <span
                                className="text-xs text-white/30"
                                style={{ fontFamily: 'JetBrains Mono' }}
                            >
                                Top 20 % {'>'}= 85
                            </span>
                        </div>
                    </div>

                    {/* Quick KPIs */}
                    <div className="grid grid-cols-2 gap-4">
                        <KpiTile label="CA encaissé / mois" value={d.ca_month} suffix=" €" color="#10B981" sparkline={[20, 25, 28, 32, 38, 42, 47]} />
                        <KpiTile label="Pipeline pondéré" value={d.pipelineValue} suffix=" €" color="#A855F7" compact sparkline={[200, 240, 260, 280, 290, 300, 312]} />
                        <KpiTile label="Mandats actifs" value={d.activeMandates} suffix="" color="#FF914D" sparkline={[15, 17, 18, 19, 20, 21, 21]} />
                        <KpiTile label="Ventes / mois" value={d.soldThisMonth} suffix="" color="#3B82F6" sparkline={[2, 2, 3, 3, 3, 4, 4]} />
                    </div>
                </motion.section>

                {/* ─── 3-COLUMN BENTO INSIGHTS ─── */}
                <section className="space-y-4">
                    <div className="flex items-center justify-between">
                        <h3
                            className="text-2xl font-semibold tracking-tight"
                            style={{ fontFamily: 'Space Grotesk' }}
                        >
                            Cette semaine, focus sur 5 axes
                        </h3>
                        <span className="text-xs text-white/40" style={{ fontFamily: 'JetBrains Mono' }}>
                            5 insights · classés par impact €
                        </span>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                        {/* Column 1 - URGENCES */}
                        <div className="space-y-4">
                            <ColumnHeader
                                title="Urgences"
                                count={d.insights.urgent.length}
                                color="#FF914D"
                                icon={AlertTriangle}
                                subtitle="À traiter sous 48 h"
                            />
                            {d.insights.urgent.map((ins, i) => (
                                <AIInsightCard
                                    key={`urg-${i}`}
                                    category="urgent"
                                    icon={ins.icon}
                                    headline={ins.headline}
                                    metricValue={ins.metricValue}
                                    metricPrefix={ins.metricPrefix}
                                    metricSuffix={ins.metricSuffix}
                                    why={ins.why}
                                    ctaLabel={ins.ctaLabel}
                                    sparkline={ins.sparkline}
                                    testId={`ai-insight-card-urgent-${i}`}
                                    delay={0.1 * i}
                                />
                            ))}
                        </div>

                        {/* Column 2 - OPPORTUNITÉS */}
                        <div className="space-y-4">
                            <ColumnHeader
                                title="Opportunités"
                                count={d.insights.opportunity.length}
                                color="#A855F7"
                                icon={Target}
                                subtitle="Gain mensuel projeté"
                            />
                            {d.insights.opportunity.map((ins, i) => (
                                <AIInsightCard
                                    key={`opp-${i}`}
                                    category="opportunity"
                                    icon={ins.icon}
                                    headline={ins.headline}
                                    metricValue={ins.metricValue}
                                    metricPrefix={ins.metricPrefix}
                                    metricSuffix={ins.metricSuffix}
                                    why={ins.why}
                                    ctaLabel={ins.ctaLabel}
                                    sparkline={ins.sparkline}
                                    testId={`ai-insight-card-opportunity-${i}`}
                                    delay={0.1 * i + 0.2}
                                />
                            ))}
                        </div>

                        {/* Column 3 - WINS */}
                        <div className="space-y-4">
                            <ColumnHeader
                                title="Wins"
                                count={d.insights.win.length}
                                color="#10B981"
                                icon={Award}
                                subtitle="À célébrer en réunion"
                            />
                            {d.insights.win.map((ins, i) => (
                                <AIInsightCard
                                    key={`win-${i}`}
                                    category="win"
                                    icon={ins.icon}
                                    headline={ins.headline}
                                    metricValue={ins.metricValue}
                                    metricPrefix={ins.metricPrefix}
                                    metricSuffix={ins.metricSuffix}
                                    why={ins.why}
                                    ctaLabel={ins.ctaLabel}
                                    sparkline={ins.sparkline}
                                    testId={`ai-insight-card-win-${i}`}
                                    delay={0.1 * i + 0.4}
                                />
                            ))}
                        </div>
                    </div>
                </section>

                {/* ─── SIGNATURE FOOTER ─── */}
                <footer className="flex flex-col md:flex-row items-center justify-between gap-4 pt-8 pb-12 border-t border-white/5">
                    <div className="flex items-center gap-3 text-xs text-white/40">
                        <div className="flex items-center gap-1.5">
                            <Sparkles className="w-3.5 h-3.5 text-[#FF914D]" />
                            <span style={{ fontFamily: 'Space Grotesk' }}>
                                Analyse propulsée par <span className="text-white/70 font-semibold">Pilote · IA Homly</span>
                            </span>
                        </div>
                        <span className="w-1 h-1 rounded-full bg-white/20" />
                        <span style={{ fontFamily: 'JetBrains Mono' }}>
                            <Clock className="w-3 h-3 inline mr-1" />
                            Mise à jour il y a 2 min
                        </span>
                    </div>
                    <span
                        className="text-xs text-white/30"
                        style={{ fontFamily: 'JetBrains Mono' }}
                    >
                        Prochaine analyse · Lundi 7h
                    </span>
                </footer>
            </main>
        </div>
    );
}

/* ────────────── Sub-components ────────────── */

function KpiTile({ label, value, suffix, color, sparkline, compact }) {
    return (
        <div className="p-4 rounded-xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] transition-colors">
            <p
                className="text-[10px] uppercase tracking-widest text-white/40 mb-2"
                style={{ fontFamily: 'Space Grotesk' }}
            >
                {label}
            </p>
            <div className="flex items-baseline gap-1">
                <span
                    className="text-2xl font-medium tabular-nums text-white"
                    style={{ fontFamily: 'JetBrains Mono', color }}
                >
                    <AnimatedCounter value={value} format={compact ? 'compact' : 'fr'} />
                </span>
                <span
                    className="text-sm opacity-60"
                    style={{ fontFamily: 'JetBrains Mono', color }}
                >
                    {suffix}
                </span>
            </div>
            <div className="mt-2">
                <SparklineChart data={sparkline} color={color} width={140} height={20} showDot={false} />
            </div>
        </div>
    );
}

function ColumnHeader({ title, count, color, icon: Icon, subtitle }) {
    return (
        <div className="flex items-center justify-between pb-2 border-b border-white/5">
            <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center"
                    style={{ background: `${color}1a`, border: `1px solid ${color}33` }}>
                    <Icon className="w-3.5 h-3.5" style={{ color }} />
                </div>
                <div>
                    <p
                        className="text-sm font-semibold text-white tracking-tight"
                        style={{ fontFamily: 'Space Grotesk' }}
                    >
                        {title}
                        <span
                            className="ml-2 text-xs text-white/30"
                            style={{ fontFamily: 'JetBrains Mono' }}
                        >
                            · {count}
                        </span>
                    </p>
                    <p
                        className="text-[10px] uppercase tracking-widest text-white/30"
                        style={{ fontFamily: 'Space Grotesk' }}
                    >
                        {subtitle}
                    </p>
                </div>
            </div>
        </div>
    );
}
